import { AxiosError } from 'axios'
import { http } from '@/services/api'

/** 'backup' states are created automatically before a reset. */
export type ProjectStateSource = 'pdf' | 'export' | 'backup'

export interface ProjectStateItem {
  id: string
  projectId: string
  timestamp: number
  createdAt: string
  source: ProjectStateSource
  label: string | null
  appVersion: string | null
  userId: string
  pdfId: string | null
}

export interface ProjectStateDetail extends ProjectStateItem {
  meta: Record<string, unknown>
  project: Record<string, unknown>
}

export interface CreateProjectStatePayload {
  projectId: string
  userId: string
  source: ProjectStateSource
  meta: Record<string, unknown>
  project: Record<string, unknown>
  label?: string | null
  appVersion?: string | null
  pdfId?: string | null
}

function authHeaders(refreshToken: string) {
  return { headers: { Authorization: `Bearer ${refreshToken}` } }
}

export async function createProjectState(
  payload: CreateProjectStatePayload,
  refreshToken: string
): Promise<ProjectStateItem> {
  const response = await http.post('/project-state', payload, authHeaders(refreshToken))
  return response.data.state as ProjectStateItem
}

export async function fetchProjectStates(projectId: string, refreshToken: string): Promise<ProjectStateItem[]> {
  const response = await http.get('/project-state', { params: { projectId }, ...authHeaders(refreshToken) })
  return (response.data.states as ProjectStateItem[] | undefined) ?? []
}

export async function fetchProjectState(id: string, refreshToken: string): Promise<ProjectStateDetail> {
  const response = await http.get(`/project-state/${encodeURIComponent(id)}`, authHeaders(refreshToken))
  return response.data.state as ProjectStateDetail
}

export async function renameProjectState(id: string, label: string, refreshToken: string): Promise<ProjectStateItem> {
  const response = await http.patch(`/project-state/${encodeURIComponent(id)}`, { label }, authHeaders(refreshToken))
  return response.data.state as ProjectStateItem
}

/** Deletes the state together with its summary PDF. */
export async function deleteProjectState(id: string, refreshToken: string): Promise<void> {
  await http.delete(`/project-state/${encodeURIComponent(id)}`, authHeaders(refreshToken))
}

/** Resolves a share-file reference. Returns null when no state matches. */
export async function resolveProjectState(
  projectId: string,
  timestamp: number,
  refreshToken: string
): Promise<ProjectStateDetail | null> {
  try {
    const response = await http.get('/project-state', {
      params: { projectId, timestamp },
      ...authHeaders(refreshToken),
    })
    return (response.data.state as ProjectStateDetail | undefined) ?? null
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return null
    }
    throw error
  }
}
